import React, { useEffect, useRef, useState } from 'react';
import { Button, Result, Spin } from 'antd';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { isIos } from './InstallPrompt';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Inline PDF preview.
 *
 * Everywhere except iOS an <iframe> is the best option: it uses the browser's
 * own PDF plugin, which is faster and gives the user print/zoom for free.
 *
 * iOS Safari is the exception — it renders only the first page of a PDF in an
 * iframe and will not scroll it, and it ignores the `#view=FitH&toolbar=0`
 * fragment entirely. So on iOS we render every page ourselves with react-pdf
 * (already a dependency, already used by PdfPreviewModal).
 */
const PdfViewer = ({ url, title = 'PDF', onDownload }) => {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [width, setWidth] = useState(0);
  const [error, setError] = useState(false);
  const useCanvas = isIos();

  // react-pdf needs an explicit pixel width to fit pages to the container.
  useEffect(() => {
    if (!useCanvas) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;

    const measure = () => setWidth(el.clientWidth);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [useCanvas]);

  useEffect(() => {
    setError(false);
    setNumPages(0);
  }, [url]);

  if (!url) return null;

  if (!useCanvas) {
    return (
      <iframe
        src={`${url}#view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
        title={title}
        style={{ width: '100%', height: '100%', border: 'none' }}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        background: '#525659',
      }}
    >
      {error ? (
        <Result
          status="warning"
          title="Could not display this PDF"
          subTitle="Download it to open in another app."
          extra={
            onDownload && (
              <Button type="primary" onClick={onDownload}>
                Download PDF
              </Button>
            )
          }
        />
      ) : (
        <Document
          file={url}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          onLoadError={() => setError(true)}
          loading={
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <Spin size="large" />
            </div>
          }
          error={null}
        >
          {width > 0 &&
            Array.from({ length: numPages }, (_, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                <Page
                  pageNumber={i + 1}
                  width={width - 16}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </div>
            ))}
        </Document>
      )}
    </div>
  );
};

export default PdfViewer;
